/**
 * Parser CSV mic (RFC 4180): câmpuri între ghilimele, ghilimele dublate,
 * separatori și linii noi în interiorul unui câmp, terminatori CRLF / LF / CR.
 * Separatorul (virgulă, punct și virgulă sau tab) se deduce din antet.
 */
import { TransferError } from '@/features/vault/transfer/types';

const DELIMITERS = [',', ';', '\t'] as const;

/** Separatorul care apare cel mai des pe prima linie, în afara ghilimelelor. */
function detectDelimiter(input: string): string {
  const counts = new Map<string, number>(DELIMITERS.map((d) => [d, 0]));
  let quoted = false;
  for (const ch of input) {
    if (ch === '"') quoted = !quoted;
    else if (!quoted && (ch === '\n' || ch === '\r')) break;
    else if (!quoted && counts.has(ch)) counts.set(ch, (counts.get(ch) ?? 0) + 1);
  }
  let best: string = DELIMITERS[0];
  for (const d of DELIMITERS) {
    if ((counts.get(d) ?? 0) > (counts.get(best) ?? 0)) best = d;
  }
  return best;
}

/** Întoarce rândurile ca liste de câmpuri; rândurile complet goale sunt omise. */
export function parseCsv(raw: string): string[][] {
  const input = raw.charCodeAt(0) === 0xfeff ? raw.slice(1) : raw;
  const delimiter = detectDelimiter(input);
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  // Un câmp care a început cu ghilimele rămâne câmp și dacă e gol (`""`).
  let wasQuoted = false;

  const endField = () => {
    row.push(field);
    field = '';
    wasQuoted = false;
  };
  const endRow = () => {
    endField();
    if (row.length > 1 || row[0] !== '') rows.push(row);
    row = [];
  };

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (quoted) {
      if (ch !== '"') field += ch;
      else if (input[i + 1] === '"') {
        field += '"';
        i++;
      } else quoted = false;
    } else if (ch === '"' && field === '' && !wasQuoted) {
      quoted = true;
      wasQuoted = true;
    } else if (ch === delimiter) {
      endField();
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && input[i + 1] === '\n') i++;
      endRow();
    } else {
      field += ch;
    }
  }
  if (quoted) throw new TransferError('csv_malformed');
  if (field !== '' || wasQuoted || row.length > 0) endRow();
  return rows;
}
